import React, { useState, useEffect } from 'react';

const title = 'Budget Overview'; // Page Title
const description = `The budget feature acts as your
 financial co-pilot, giving you real-time alerts 
 to keep your spending on track. It doesn't automatically
  stop you from spending, but it's a powerful tool that simply
   tells you if you are spending too much in a certain 
   category or if your project is at risk of going over 
   budget. By providing this timely information, it empowers 
   you to make informed decisions and take control of your finances
    before any issues get out of hand, ensuring you can manage 
    your money effectively to achieve your goals.`; // Page Description

const page_images = [

    '/images/budget/budget-overview.png', // 0
    '/images/budget/new.png', // 1
    '/images/budget/new-budget-box.png', // 2
    '/images/budget/detail-account.png', // 3
    '/images/budget/save-budget.png', // 4
    '/images/budget/run-all-budget.png', // 5
    '/images/budget/budget-overview.png', // 6
    '/images/budget/search.png', // 7
    '/images/budget/search-box.png', // 8
    '/images/budget/budget-detail/budget-detail-overview.png', // 9
    '/images/budget/budget-detail/edit-button.png', // 10
    '/images/budget/budget-detail/detail-edit-box.png', // 11
    '/images/budget/budget-detail/budget-adjustment.png', // 12
    '/images/budget/budget-detail/run-budget.png', // 13
    '/images/budget/budget-detail/print-document.png', // 14
    '/images/budget/budget-detail/comment-history.png', // 15
    '/images/budget/budget-detail/comment-history-box.png', // 16
    '/images/budget/budget-detail/adjustment.png', // 17
    '/images/budget/budget-detail/adjustment-box.png', // 18

]; // all page's images

const topic = [
    {
        title: 'Home',
        description: ``,
        id: 'home',
    },
    {
        title: '+ New',
        description: ``,
        id: 'new-account',
    },
    {
        title: 'Run All Budget',
        description: ``,
        id: 'run-all-budget',
    },
    {
        title: 'Search',
        description: ``,
        id: 'search',
    },
    {
        title: 'View Details',
        description: ``,
        id: 'view-details',
    },

]; // topic for page

const available_action = [topic[2],topic[3],topic[4]];

const available_action_description =  `${title} Page offers several action buttons in the top toolbar :`
const BudgetOverview: React.FC = () => {

    //  Topic nav active state
    const [activeId, setActiveId] = useState(topic[0].id);

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        const newActiveId = entry.target.id;
                        setActiveId(newActiveId);
                        window.history.replaceState(null, '', `#${newActiveId}`);
                    }
                });
            },
            {
                root: null,
                rootMargin: '-50% 0% -50% 0%',
                threshold: 0,
            }
        );

        topic.forEach((item) => {
            const section = document.getElementById(item.id);
            if (section) {
                observer.observe(section);
            }
        });

        return () => {
            observer.disconnect();
        };
    }, []);

    const handleItemClick = (id: string) => {
        setActiveId(id);
    };

    return (
        <div className="max-w-4xl min-h-screen bg-gray-50 flex gap-2">
            <div className="overflow-auto mx-auto flex-2">

                {/* Chart of Accounts */}
                <div className="mb-6" id={topic[0].id}>
                    <h1 className="text-3xl font-bold pt-4 text-gray-900">
                        {title}
                    </h1>
                    <p className="text-gray-600 mt-4">
                        {description}
                    </p>
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 my-6">
                        <img
                            src={page_images[0]}
                            alt={title}
                            className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                    </div>
                </div>

                {/* Available action */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">Available Actions</h2>
                    <p className="text-gray-600 mb-4">
                        {available_action_description}
                    </p>
                    <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
                        {
                            available_action.map((item,index)=>(
                                <li key={index}><strong>{item.title}</strong></li>
                            ))
                        }
                    </ul>

                </div>

                {/* New Budget */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" id={topic[1].id}>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{topic[1].title}</h2>
                    <p className="text-gray-600 mb-4">
                        To create a new budget, just click the + New button.
                    </p>
                    <img
                        src={page_images[1]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                    <p className="text-gray-600 mb-4 pt-4">
                        After you click the + New button, a new window will pop up to guide you through creating your budget.
                        To create your new budget, you must fill in all the required fields marked with a red asterisk (*).
                    </p>
                    <img
                        src={page_images[2]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                    <p className="text-gray-600 mb-4 pt-4">
                        In the Detail Account section, simply check the boxes next to the accounts you want to include in your budget.
                    </p>
                    <img
                        src={page_images[3]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                    <p className="text-gray-600 mb-4 pt-4">
                        Once you have entered all the necessary information, you click Save button to save your new budget and finalize all the details you've entered.
                    </p>
                    <img
                        src={page_images[4]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                </div>

                {/* Run all Budgets */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" id={topic[2].id}>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{topic[2].title}</h2>
                    <p className="text-gray-600 mb-4">
                        After you've saved your budget, click the Run All Budgets button to start tracking your spending against your new plan.
                    </p>
                    <img
                        src={page_images[5]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                </div>

                {/* Search */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" id={topic[3].id}>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{topic[3].title}</h2>
                    <p className="text-gray-600 mb-4">
                        Use the search button to find any budget you need.
                    </p>
                    <img
                        src={page_images[7]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                    <p className="text-gray-600 mb-4 pt-4">
                        When you click the search button, a search box will appear where you can find any budget by Name, Status, Branch, Start Date, or End Date.
                    </p>
                    <img
                        src={page_images[8]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                </div>

                {/* View Detail */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6" id={topic[4].id}>
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">{topic[4].title}</h2>
                    <p className="text-gray-600 mb-4">
                        Clicking on any budget will take you to a detailed overview page where you can see all the specifics.
                        On the budget details page, you'll see your budget broken down by month. Each monthly budget is either approved
                        or a draft. The key difference is that a draft is still editable, while an approved budget cannot be changed.
                    </p>
                    <img
                        src={page_images[9]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>

                    {/* Edit */}
                    <h2 className="text-md font-semibold text-gray-900 mt-6">Edit Budget</h2>
                    <p className="text-gray-600 mb-4 pt-2">
                        To make changes to a draft budget, simply click the Edit button.
                    </p>
                    <img
                        src={page_images[10]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                    <p className="text-gray-600 mb-4 pt-4">
                        Clicking the Edit button will open the edit budget box, making it simple and easy to make any changes you need.
                    </p>
                    <img
                        src={page_images[11]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>


                    {/* Run Budget */}
                    <h2 className="text-md font-semibold text-gray-900 mt-6">Run Budget</h2>

                    <p className="text-gray-600 mb-4 pt-4">
                        After you've edit your budget, click the Run Budgets button to start tracking your spending against your new plan.
                    </p>
                    <img
                        src={page_images[13]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>



                    {/* Print Budget */}
                    <h2 className="text-md font-semibold text-gray-900 mt-6">Print Budget</h2>

                    <p className="text-gray-600 mb-4 pt-4">
                        To print a document of your budget, click the Print button.
                    </p>
                    <img
                        src={page_images[14]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>


                    {/* Comment & History */}
                    <h2 className="text-md font-semibold text-gray-900 mt-6">Comment & History</h2>
                    <p className="text-gray-600 mb-4 pt-2">
                        There are also Comment and History buttons. You can add comments to each budget and view the full history of all past comments.
                    </p>
                    <img
                        src={page_images[15]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                    <p className="text-gray-600 mb-4 pt-4">
                        Clicking the Comment and History button will open the Comment and History box, making it simple and easy to make any changes you need.
                    </p>
                    <img
                        src={page_images[16]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>


                    {/* Budget Adjustment */}
                    <h2 className="text-md font-semibold text-gray-900 mt-6">Budget Adjustment</h2>

                    <p className="text-gray-600 mb-4 pt-4">
                        If you need to change a budget, you can use the Budget Adjustment feature.
                    </p>
                    <img
                        src={page_images[12]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                    <p className="text-gray-600 mb-4 pt-4">
                        Clicking the Budget Adjustment button will open a new box where you can make changes to your budget.
                    </p>
                    <img
                        src={page_images[18]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                    <p className="text-gray-600 mb-4 pt-4">
                        To adjust the budget, simply select the budget account you want to change, enter the new amount, and then save your changes.
                    </p>
                    <img
                        src={page_images[17]}
                        alt={title}
                        className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm mx-auto"/>
                </div>
            </div>

            {/* side route */}
            <div className="fixed w-1/5 right-2">
                <ul>
                    {topic.map((item) => (
                        <li key={item.id} className="mb-4">
                              <span className="text-xs">
                                    <a
                                        className={`hover:text-blue-700 border-l p-4 ${
                                            activeId === item.id ? 'text-blue-700 font-bold border-blue-700' : ''
                                        }`}
                                        href={`#${item.id}`}
                                        onClick={() => handleItemClick(item.id)}
                                    >
                                      {item.title}
                                    </a>
                              </span>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default BudgetOverview;
